export type StyleTileOption = {
    value: string;
    name: string;
    description?: string;
    imageUrl: string;
};

export type StyleTilesOptions = {
    title?: string;
    options: StyleTileOption[];
    preselected?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseStyleTileOption(item: unknown): StyleTileOption | null {
    if (!isRecord(item)) {
        return null;
    }

    const { value, name, description, imageUrl } = item;

    if (typeof value !== 'string' || value === '') {
        return null;
    }
    if (typeof name !== 'string' || name === '') {
        return null;
    }
    if (typeof imageUrl !== 'string' || imageUrl === '') {
        return null;
    }
    if (description !== undefined && typeof description !== 'string') {
        return null;
    }

    return description === undefined
        ? { value, name, imageUrl }
        : { value, name, imageUrl, description };
}

/**
 * Parses the assistant-side style-tiles question payload that drives the composer selector.
 *
 * Accepts `unknown` input and returns a validated `StyleTilesOptions` or `null` on any
 * validation failure. `title` and `preselected` are optional and may be entirely absent — the
 * live payload carries neither. Lenient on unknown fields; only known fields are extracted.
 *
 * @param data - The input to parse (expected to be the assistant's `component.data`)
 * @returns A validated `StyleTilesOptions` object, or `null` if validation fails
 */
export function parseStyleTilesOptions(data: unknown): StyleTilesOptions | null {
    if (!isRecord(data)) {
        return null;
    }

    const { title, options, preselected } = data;

    if (!Array.isArray(options) || options.length === 0) {
        return null;
    }

    const parsedOptions: StyleTileOption[] = [];
    for (const item of options) {
        const parsed = parseStyleTileOption(item);
        if (!parsed) {
            return null;
        }
        parsedOptions.push(parsed);
    }

    if (title !== undefined && typeof title !== 'string') {
        return null;
    }
    if (preselected !== undefined && typeof preselected !== 'string') {
        return null;
    }

    return {
        ...(title !== undefined ? { title } : {}),
        options: parsedOptions,
        ...(preselected !== undefined ? { preselected } : {}),
    };
}
